import { createStore } from "vuex";
import axiosClient from "../axios";
import {getCookie, removeCookie} from '@/cookieUtils';
import {getCurrentI18n} from "@/locales/i18n";
import router from "@/routes";

const store = createStore({
  state: {
    user: {
      data: {},
      token: getCookie("auth_token"),
      isMicrosoftAuthenticated: false,
      isAuthenticated: false,
      authenticate_loading: false,
      jwt_token: getCookie("jwt_token"),
      permissions: (() => {
        try {
          return JSON.parse(getCookie("permissions") || '{}') || [];
        } catch (error) {
          console.error('Failed to parse permissions:', error);
          return [];
        }
      })(),    },
    dashboard: {
      loading: false,
      data: {}
    },
    public_data: {
      loading: false,
      data: {}
    },
    personnel_potentials: {
      loading: false,
      links: [],
      data: []
    },
    qualitative_composition: {
      loading: false,
      links: [],
      data: []
    },
    student_by_degree: {
      loading: false,
      links: [],
      data: []
    },
    questionTypes: ["text", "select", "radio", "checkbox", "textarea"],
    avatar: '',
    surveys: {
      loading: false,
      links: [],
      data: []
    },
    currentSurvey: {
      data: {},
      loading: false,
    },
    scale: localStorage.getItem("scale") ? localStorage.getItem("scale") : 14,
    notification: {
      show: false,
      type: 'success',
      message: ''
    }
  },
  getters: {
    isLogged() {
      return !!getCookie("auth_token");
    },
    checkPermission: (state) => (model) => {
      let result = [];
      let permissions = []
      for (let i = 0; i < model.length; i++) {
        // Check if permissions exist and is an array before iterating
        if (state.user.permissions && Array.isArray(state.user.permissions)) {
          permissions = state.user.permissions;
          state.user.permissions.forEach(function (permission) {
            if (permission === model[i]) {
              result.push(true);
            } else {
              result.push(false);
            }
          });
        }
      }
      let hasPermission = result.includes(true);
      return(
        permissions.includes('all') || hasPermission
      );
    },
    },

    actions: {
    login({commit}, user) {
      return axiosClient.post('/login', user)
        .then(({data}) => {
          commit('setUser', data.user);
          commit('setToken', data.token)
          return data;
        })
    },
    authenticate({commit}, user) {
      commit('setAuthenticateLoading', true);
      return axiosClient.post('/authenticate', user)
        .then(({data}) => {
          commit('setAuthenticateLoading', true);
          commit('setUser', data.user);
          commit('setUserIsAuth', true);
          commit('setToken', data.token)
          commit('setJWTToken', data.jwt_token)
          commit('setPermissions', data.permissions)
          commit('setAuthenticateLoading', false);
          return data;
        })
    },
    login_with_ssh({commit}, iin) {
      return axiosClient.post('/login_with_ssh', iin)
        .then(({data}) => {
          commit('setUser', data.user);
          commit('setToken', data.token)
          return data;
        })
    },
    changeAvatar({commit},form) {
      return axiosClient.post('/avatar/change', form)
        .then(({data}) => {
          commit('setAvatar', data.data.avatar_src);
          return data;
        })
    },
    getDashboardData({commit}) {
      commit('dashboardLoading', true)
      return axiosClient.get(`/dashboard`)
        .then((res) => {
          commit('dashboardLoading', false)
          commit('setDashboardData', res.data)
          return res;
        })
        .catch(error => {
          commit('dashboardLoading', false)
          return error;
        })
    },
    getPublicData({commit}) {
      commit('publicDataLoading', true)
      return axiosClient.get(`/public_data`)
        .then((res) => {
          commit('publicDataLoading', false)
          commit('setPublicDataData', res.data)
          return res;
        })
        .catch(error => {
          commit('publicDataLoading', false)
          return error;
        })
    },
    getReports() {
      return axiosClient.get(`/reports`)
        .then(request => request.data)
        .catch(error => {
          return error;
        })
    },
    getPersonnelPotentials({ commit }, {url = null} = {}) {
      commit('setPersonnelPotentialsLoading', true)
      url = url || "/personnel_potential";
      return axiosClient.get(url).then((res) => {
        commit('setPersonnelPotentialsLoading', false)
        commit("setPersonnelPotentials", res.data);
        return res;
      });
    },
    getStudentByDegree({ commit }, {url = null} = {}) {
      commit('setStudentByDegreesLoading', true)
      url = url || "/student_by_degree";
      return axiosClient.get(url).then((res) => {
        commit('setStudentByDegreesLoading', false)
        commit("setStudentByDegrees", res.data);
        return res;
      });
    },
    getQualitativeCompositions({ commit }, {url = null} = {}) {
      commit('setQualitativeCompositionsLoading', true)
      url = url || "/qualitative_composition";
      return axiosClient.get(url).then((res) => {
        console.log(res.data)
        commit('setQualitativeCompositionsLoading', false)
        commit("setQualitativeCompositions", res.data);
        return res;
      });
    },
    logout({commit}) {
      router.push({ name: "loading", params: { locale: getCurrentI18n() } });
      return axiosClient.post('/logout')
        .then(response => {
          commit('logout')
          return response;
        })
    },
    getUser({commit}) {
      return axiosClient.get('/user')
      .then(res => {
        commit('setUser', res.data)
      })
    },
  },
  mutations: {
    logout: (state) => {
      state.user.token = null;
      state.user.jwt_token = null;
      state.user.permissions = null;
      state.user.isMicrosoftAuthenticated = false;
      state.user.isAuthenticated = false;
      state.user.data = {};
      localStorage.removeItem("access_token");
      removeCookie("auth_token")
    },

    setUser: (state, user) => {
      state.user.data = user;
    },
    setAuthenticateLoading: (state, loading) => {
      state.user.authenticate_loading = loading;
    },
    setAvatar: (state, avatar) => {
      state.user.data.avatar_src = avatar;
      state.user.data.avatar = avatar;
    },
    setToken: (state, token) => {
      state.user.token = token;
      const expirationDate = new Date();

      expirationDate.setSeconds(expirationDate.getSeconds() + 3600);

      document.cookie = `auth_token=${token}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax; Secure`;
      router.push({ name: "RealHome", params: { locale: getCurrentI18n() } });

    },
    setJWTToken: (state, jwt_token) => {
      state.user.jwt_token = jwt_token;
      const expirationDate = new Date();
      expirationDate.setSeconds(expirationDate.getSeconds() + 3600);
      document.cookie = `jwt_token=${jwt_token}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax; Secure`;

    },
    setPermissions: (state, permissions) => {
      state.user.permissions = permissions;
      const expirationDate = new Date();
      expirationDate.setSeconds(expirationDate.getSeconds() + 3600);
      document.cookie = `permissions=${JSON.stringify(permissions)}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax; Secure`;
    },

    setUserIsAuth: (state, bool) => {
      state.user.isAuthenticated = bool;
    },
    dashboardLoading: (state, loading) => {
      state.dashboard.loading = loading;
    },
    publicDataLoading: (state, loading) => {
      state.public_data.loading = loading;
    },
    setDashboardData: (state, data) => {
      state.dashboard.data = data
    },
    setPublicDataData: (state, data) => {
      state.public_data.data = data
    },
    setPersonnelPotentialsLoading: (state, loading) => {
      state.personnel_potentials.loading = loading;
    },
    setPersonnelPotentials: (state, personnel_potentials) => {
      state.personnel_potentials.links = personnel_potentials.links;
      state.personnel_potentials.data = personnel_potentials.data;
    },
    setStudentByDegreesLoading: (state, loading) => {
      state.student_by_degree.loading = loading;
    },
    setStudentByDegrees: (state, student_by_degree) => {
      state.student_by_degree.links = student_by_degree.links;
      state.student_by_degree.data = student_by_degree.data;
    },
    setQualitativeCompositionsLoading: (state, loading) => {
      state.qualitative_composition.loading = loading;
    },
    setQualitativeCompositions: (state, qualitative_composition) => {
      console.log(qualitative_composition.data)
      state.qualitative_composition.links = qualitative_composition.links;
      state.qualitative_composition.data = qualitative_composition.data;
    },
    setScale: (state, scale) => {
      state.scale = scale;
      localStorage.setItem('scale', scale);
    },
    notify: (state, {message, type}) => {
      state.notification.show = true;
      state.notification.type = type;
      state.notification.message = message;
      setTimeout(() => {
        state.notification.show = false;
      }, 3000)
    },
    checkPermission: (state) => (model = []) => {
      let result = [];
      for (let i=0; i<model.length; i++) {
        state.user.data.permissions.forEach(function (item) {
          result.push(item.obj === model[i]);
        });
      }

      let bool = result.includes(true);
      return(
        state.user.data.access_group[0].id === 1 || bool
      );
    },
  },
  modules: {},
});

export default store;
